import { TableHeader } from '@/src/components/common/TableHeader';
import { KeyCode } from '@amzn/awsui-component-toolkit/internal';
import {
  Button,
  Container,
  ExpandableSection,
  Header,
  Icon,
  Input,
  SpaceBetween,
  Table,
  Toggle,
} from '@amzn/awsui-components-react';
import _, { uniq } from 'lodash';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { EditCampaignActions, useEditCampaign } from '../../../../store/edit-campaign.context';
import { EditEventActions, useEditEvent } from '../../../../store/edit-event.context';
import { useFlashbars } from '../../../../store/flashbar.context';
import { useUser } from '../../../../store/user.context';
import { Campaign } from '../../../../types/Campaign';
import { Event, EventPermission, EventPermissionType } from '../../../../types/Event';
import { copyToClipboard } from '../../../../utils/copy-to-clipboard';
import { i18nKeys } from '../../../../utils/i18n.utils';
import { fromPlainObject } from '../../../../utils/mapper.utils';
import { successFlashbar } from '../../../../utils/notification.utils';
import { isEmailValid, splitDelimitedEmails } from '../../../../utils/string.utils';

interface OwnerPermissionsProps {
  target: Event | Campaign | undefined;
  targetType: 'event' | 'campaign';
}

const OwnerPermissions: React.FC<OwnerPermissionsProps> = ({ target }) => {
  const [eventPermissions, setEventPermissions] = useState<EventPermission[]>([]);
  const { user } = useUser();
  const { addFlashbar } = useFlashbars();
  const { editMode, newEventMode, isGuestUserAndEditModeEnabled, handleUpdateEditEvent } = useEditEvent();
  const { campaignEditMode, handleUpdateEditCampaign, newCampaignMode } = useEditCampaign();
  const { t } = useTranslation();
  const [errorMessage, setErrorMessage] = useState('');
  const [newEmail, setNewEmail] = useState('');
  const [creatorEmail, setCreatorEmail] = useState('');

  const canEdit = editMode || campaignEditMode || newEventMode || newCampaignMode;
  const isEvent = target instanceof Event;

  useEffect(() => {
    const permissions = isEvent ? target?.eventPermissions : target?.campaignSettings?.eventPermissions;
    const eventPermissionList: EventPermission[] = permissions?.filter((ep: { email: any }) => ep?.email) || [];
    const newCreatorEmail: string = target?.requestedBy || user?.email || '';

    // add the owner if not already there
    const users = eventPermissionList.map((ep: { email: string }) => ep.email);
    setCreatorEmail(newCreatorEmail);

    const permissionsIncludesCreator = users.includes(newCreatorEmail);
    if (!permissionsIncludesCreator) {
      eventPermissionList.unshift(
        fromPlainObject(
          {
            email: newCreatorEmail,
            eventPermissionType: EventPermissionType.OWNER,
          },
          EventPermission
        )
      );
    }

    const newEventPermissions = eventPermissionList.map(
      (permission) => fromPlainObject(permission, EventPermission) as EventPermission
    );

    setEventPermissions(_.cloneDeep(newEventPermissions));

    // Update the EditedEvent when this is a new Event to ensure eventPermissions are correctly populated
    if (!permissionsIncludesCreator && (newEventMode || newCampaignMode)) {
      if (isEvent) {
        handleUpdateEditEvent(EditEventActions.EVENT_PERMISSIONS, newEventPermissions);
      } else {
        handleUpdateEditCampaign(EditCampaignActions.EVENT_PERMISSIONS, newEventPermissions);
      }
    }
  }, [editMode, campaignEditMode, target, user]); // Reload permissions when edit mode changes or when target/user changes

  const validateAndSplitEmails = (newEmails: string): { validEmails: string[]; hasInvalidEmails: boolean } => {
    const emails: string[] = splitDelimitedEmails(newEmails);
    const invalidEmails = (emails || []).filter((e) => !isEmailValid(e));
    const validEmails = (emails || []).filter((e) => isEmailValid(e));

    if (invalidEmails.length > 0) {
      setErrorMessage(t(i18nKeys.errors.eventPermissions.invalidEmail) + invalidEmails.join(', '));
      return { validEmails, hasInvalidEmails: true };
    } else {
      setErrorMessage('');
      return { validEmails, hasInvalidEmails: false };
    }
  };

  const addEmails = () => {
    const { validEmails, hasInvalidEmails } = validateAndSplitEmails(newEmail);
    const eventPermissionList = eventPermissions;

    // Only proceed with adding emails if there are no invalid emails
    if (validEmails.length && !hasInvalidEmails) {
      let duplicateFound = false;

      validEmails.forEach((email) => {
        const emailAlreadyExists = eventPermissionList.find((item) => item.email === email);
        if (!emailAlreadyExists) {
          eventPermissionList.push(
            EventPermission.fromPlainObject({
              email,
              eventPermissionType: null,
            })
          );
        } else {
          duplicateFound = true;
          setErrorMessage(t(i18nKeys.errors.eventPermissions.existingEmail));
        }
      });

      if (!duplicateFound) {
        const newEventPermissions = _.cloneDeep(eventPermissionList);
        setEventPermissions(newEventPermissions);
        if (isEvent) {
          handleUpdateEditEvent(EditEventActions.EVENT_PERMISSIONS, newEventPermissions);
        } else {
          handleUpdateEditCampaign(EditCampaignActions.EVENT_PERMISSIONS, newEventPermissions);
        }
        setNewEmail('');
      }
    } else {
      // Don't clear the input if there were invalid emails so the user can fix them
      return;
    }
  };

  /**
   * Removes email item from list of items to add permissions to
   *
   * @param email Email to remove
   */
  const removeEmail = (email: string) => {
    const newEventPermissions = eventPermissions.filter((i) => i.email !== email);
    setEventPermissions(newEventPermissions);
    if (isEvent) {
      handleUpdateEditEvent(EditEventActions.EVENT_PERMISSIONS, newEventPermissions);
    } else {
      handleUpdateEditCampaign(EditCampaignActions.EVENT_PERMISSIONS, newEventPermissions);
    }
    setErrorMessage('');
  };

  const togglePermission = (item: EventPermission, permission: EventPermissionType) => {
    switch (permission) {
      case EventPermissionType.OWNER:
        if (!item.isOwner) {
          item.eventPermissionType = EventPermissionType.OWNER;
        } else {
          item.eventPermissionType = null;
        }
        break;
      case EventPermissionType.FACILITATOR:
        if (!item.isOwner && !item.isFacilitator) {
          item.eventPermissionType = EventPermissionType.FACILITATOR;
        } else {
          item.eventPermissionType = null;
        }
        break;
      case EventPermissionType.SUPPORT:
        if (!item.isOwner && !item.isFacilitator && !item.isSupportUser) {
          item.eventPermissionType = EventPermissionType.SUPPORT;
        } else {
          item.eventPermissionType = null;
        }
        break;
    }
    const newEventPermissions = _.cloneDeep(eventPermissions);
    setEventPermissions(newEventPermissions);
    if (isEvent) {
      handleUpdateEditEvent(EditEventActions.EVENT_PERMISSIONS, newEventPermissions);
    } else {
      handleUpdateEditCampaign(EditCampaignActions.EVENT_PERMISSIONS, newEventPermissions);
    }
  };

  const isEventCreator = (email: string) => {
    return email === creatorEmail && creatorEmail === user?.email;
  };

  /**
   * Apply the provided filter to the list of event permissions items, then return an array of all the distinct emails of the remaining items.
   *
   * @param filterFn
   * @private
   */
  const getFilteredEmails = (filterFn: (item: EventPermission) => boolean) => {
    return uniq(eventPermissions.filter(filterFn).map((item) => item.email));
  };

  /**
   * Returns the emails of all users with event owner access.
   */
  const getOwnerEmails = () => {
    return getFilteredEmails((item) => item.isOwner);
  };

  /**
   * Returns the emails of all users with facilitator access
   */
  const getFacilitatorEmails = () => {
    return getFilteredEmails((item) => item.isFacilitator);
  };

  /**
   * Join the provided array of emails with ; appended after each email.
   *
   * @param emails
   * @private
   */
  const joinEmails = (emails: string[]): string => {
    return (emails || []).join(';') + ';';
  };

  /**
   * Copy a string of emails to the clipboard for all users with event owner permission for this event.
   */
  const copyEventOwnerEmails = () => {
    copyEmailsToClipboard(joinEmails(getOwnerEmails()));
  };

  /**
   * Copy a string of emails to the clipboard for all users with facilitator permission for this event.
   * NOTE: this includes event owners because event owners have facilitator access.
   */
  const copyFacilitatorEmails = () => {
    copyEmailsToClipboard(joinEmails(getFacilitatorEmails()));
  };

  const copyEmailsToClipboard = (value: string) => {
    copyToClipboard(value);
    addFlashbar(successFlashbar(t(i18nKeys.clipboard.successCopiedToClipboard), 'Clipboard'));
  };

  const renderOwnerToggle = (permission: EventPermission, forceDisable?: boolean) => {
    if (!user) {
      return <span />;
    }

    if (!canEdit) {
      return permission?.isOwner ? <Icon name="status-positive" /> : <span />;
    }

    return (
      <Toggle
        checked={permission.isOwner}
        ariaLabel={t(i18nKeys.events.eventDetails.labels.ownerPermission)}
        onChange={() => togglePermission(permission, EventPermissionType.OWNER)}
        disabled={
          forceDisable ||
          isEventCreator(permission?.email) ||
          !target?.canEditAttribute('eventPermissions', user) ||
          isGuestUserAndEditModeEnabled
        }
        data-testid={`eventOwnerToggle-${permission.email}`}
      />
    );
  };

  const renderFacilitatorToggle = (permission: EventPermission, forceDisable?: boolean) => {
    if (!user) {
      return <span />;
    }

    if (!canEdit) {
      return permission?.isFacilitator ? <Icon name="status-positive" /> : <span />;
    }

    return (
      <Toggle
        checked={permission.isFacilitator}
        ariaLabel={t(i18nKeys.events.eventDetails.labels.facilitatorPermission)}
        onChange={() => togglePermission(permission, EventPermissionType.FACILITATOR)}
        disabled={
          forceDisable ||
          isEventCreator(permission.email) ||
          permission.isOwner ||
          !target?.canEditAttribute('eventPermissions', user) ||
          isGuestUserAndEditModeEnabled
        }
        data-testid={`eventFacilitatorToggle-${permission.email}`}
      />
    );
  };

  const renderSupportToggle = (permission: EventPermission, forceDisable?: boolean) => {
    if (!user) {
      return <span />;
    }

    if (!canEdit) {
      return permission?.isSupportUser ? <Icon name="status-positive" /> : <span />;
    }

    return (
      <Toggle
        checked={permission.isSupportUser}
        ariaLabel={t(i18nKeys.events.eventDetails.labels.supportPermission)}
        onChange={() => togglePermission(permission, EventPermissionType.SUPPORT)}
        disabled={
          forceDisable ||
          permission.isFacilitator ||
          permission.isOwner ||
          !target?.canEditAttribute('eventPermissions', user) ||
          isGuestUserAndEditModeEnabled
        }
        data-testid={`eventSupportToggle-${permission.email}`}
      />
    );
  };

  const renderContent = () => {
    return (
      <div style={{ marginBottom: '-15px' }}>
        {canEdit && (
          <div className="mb-20">
            <ul style={{ marginLeft: '-20px' }}>
              <li>
                {isEvent
                  ? t(i18nKeys.events.eventDetails.labels.eventPermissions.messages.eventOwner)
                  : t(i18nKeys.campaigns.messages.campaignDetails.ownerPermissions.campaignOwner)}
              </li>
              <li>
                {isEvent
                  ? t(i18nKeys.events.eventDetails.labels.eventPermissions.messages.mustVerify)
                  : t(i18nKeys.campaigns.messages.campaignDetails.ownerPermissions.mustVerify)}
              </li>
            </ul>
            <SpaceBetween size="xs" direction="horizontal">
              <Input
                placeholder="user@example.com"
                value={newEmail}
                onChange={({ detail }) => setNewEmail(detail.value)}
                onKeyDown={({ detail }) => {
                  if (detail.keyCode === KeyCode.enter) {
                    addEmails();
                  }
                }}
                data-testid="ownerEmailInput"
              />
              <Button className="inline" onClick={addEmails} data-testid="addOwnerEmailButton">
                {t(i18nKeys.general.add)}
              </Button>
            </SpaceBetween>
          </div>
        )}

        <Table
          data-testid="permissions-table"
          header={<TableHeader title={t(i18nKeys.events.eventDetails.headers.permissions)} />}
          items={eventPermissions}
          columnDefinitions={[
            {
              header: t(i18nKeys.events.eventDetails.headers.owner),
              cell: (item) => <span data-testid={`owner-${item.email}`}>{item.email}</span>,
            },
            {
              header: isEvent
                ? t(i18nKeys.events.eventDetails.headers.eventOwner)
                : t(i18nKeys.campaigns.headers.campaignDetails.campaignOwner),
              cell: (item) => renderOwnerToggle(item),
            },
            {
              header: t(i18nKeys.events.eventDetails.headers.facilitator),
              cell: (item) => renderFacilitatorToggle(item),
            },
            {
              header: t(i18nKeys.events.eventDetails.headers.support),
              cell: (item) => renderSupportToggle(item),
            },
            {
              header: t(i18nKeys.events.eventDetails.headers.action),
              cell: (item) => <Button iconName="close" variant="inline-icon" onClick={() => removeEmail(item.email)} />,
            },
          ]}
        />
        {!canEdit && (
          <div className="table-row-border">
            <SpaceBetween direction="horizontal" size="s">
              <Button iconName="copy" onClick={copyEventOwnerEmails}>
                {isEvent
                  ? t(i18nKeys.events.eventDetails.labels.eventPermissions.buttons.copyEventOwnerEmails)
                  : t(i18nKeys.campaigns.buttons.copyCampaignOwnerEmails)}
              </Button>
              <Button iconName="copy" onClick={copyFacilitatorEmails}>
                {t(i18nKeys.events.eventDetails.labels.eventPermissions.buttons.copyFacilitatorEmails)}
              </Button>
            </SpaceBetween>
          </div>
        )}
      </div>
    );
  };

  if (!newEventMode && !newCampaignMode) {
    return (
      <ExpandableSection
        variant="container"
        header={<Header variant="h2">{t(i18nKeys.events.eventDetails.headers.ownersAndPermissions)}</Header>}
        data-testid="permissions-expandable"
      >
        {renderContent()}
      </ExpandableSection>
    );
  }

  return (
    <Container header={<Header variant="h2">{t(i18nKeys.events.eventDetails.headers.ownersAndPermissions)}</Header>}>
      {renderContent()}
    </Container>
  );
};

export default OwnerPermissions;
